import React from 'react';

// libraries
import { Row, Col, Button } from 'react-bootstrap';

// components
import { CustomSpinner } from '_components/atoms';
import PassengerStatusBadge from './passenger-status-badge';
import ConnectionBadge from '../utils/connection-badge';

// utils
import { processAssistance, getPaxStatus, processName } from '_utils/functions';

// redux slices
import { unRejectPax } from '_redux/slices/timeline/timeline-data';
import { setSelectedFlightToView, setShowEditTaskModal } from '_redux/slices/timeline/selected-task';
import { setSelectedViewPassenger, setPassengerModal } from '_redux/slices/timeline/timeline-control';
import { openPaxCheckInModal, setClickedJob } from '_redux/slices/utils/menu-context';

/**
 * PassengerRow Component
 *
 * Renders a single passenger row with name, assistance, status, and action buttons.
 * Also handles check-in location selection when a passenger is being checked in.
 */
const PassengerRow = ({
    passenger,
    isDeparture,
    usedTheme = {},
    dispatch,
    selectedFlightToView,
    iata,
    isJersey,
    passengerToCheckOut,
    arrivalPaxCheckInStatus,
    paxCheckOutStatus,
    handleCheckOut,
    unRejectPaxStatus,
    passengerToUnReject,
    setPassengerToUnReject,
    rejectReasons,
    onAssign,
    isInAssignList,
}) => {
    //#region event handlers
    /**
     * Handle passenger view action
     * @param {object} pax - Passenger to view
     */
    const handleViewPassenger = (pax) => {
        dispatch(setSelectedFlightToView(selectedFlightToView));
        // use the original booking id to find the full booking object
        dispatch(setSelectedViewPassenger(pax));
        dispatch(setPassengerModal(true));
    };

    const handleUnRejectPax = (passenger, unRejectType = null) => {
        setPassengerToUnReject(passenger);
        let bookingId = passenger?.originalBookingId ? passenger?.originalBookingId : passenger?._id;
        const payload = {
            iata: iata,
            body: {
                bookingsIDS: [bookingId],
            }
        }
        if (unRejectType) {
            payload.body.unRejectType = unRejectType;
        }
        dispatch(unRejectPax(payload));
    };
    const checkIfCheckedInPax = (pax) => {

        if (isDeparture && pax?.paxPositionInfo?.activity === "Checked-in") {
            return true;
        }

        if ((!isDeparture && pax?.arrivalPaxCheckIn) || (!isDeparture && pax?.originalBookingDoc?.arrivalPaxCheckIn?.locationId)) {
            return true;
        }
        return false;
    }

    /**
     * Handle passenger edit action - opens the edit booking modal
     * @param {object} pax - Passenger to edit
     */
    const handleEditPassenger = (pax) => {
        // Set the passenger/job data for the edit modal
        dispatch(setClickedJob(pax));
        // Open the edit modal
        dispatch(setShowEditTaskModal(true));
    };

    const handleCheckIn = (pax) => {
        dispatch(openPaxCheckInModal(true));
        dispatch(setClickedJob(pax));
    };

    const checkIfRejectedOrServiced = () => {
        if (getPaxStatus(passenger, isDeparture ? "preBoard" : "ramp", rejectReasons)?.startsWith("All Journey Rejected")) return true;
        if (getPaxStatus(passenger, isDeparture ? "boarding" : "arrival", rejectReasons)?.startsWith("All Journey Rejected")) return true;
        if (isDeparture && getPaxStatus(passenger, "preBoard", rejectReasons)?.startsWith("Rejected")) return true;
        if (!isDeparture && getPaxStatus(passenger, "arrival", rejectReasons)?.startsWith("Rejected")) return true;
        if (!isDeparture && getPaxStatus(passenger, "arrival", rejectReasons)?.startsWith("Checked-in")) return false;
        if (!isDeparture && !getPaxStatus(passenger, "arrival", rejectReasons)?.startsWith("Waiting for Service")) return true;

        return false;
    }
    //#endregion

    //#region render

    return (
        <Row style={{ padding: '12px 16px', alignItems: 'center' }}>
            {/* Passenger Name */}
            <Col style={{ flex: '0 0 12.5%', maxWidth: '12.5%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: usedTheme?.TEXT_COLOR || '#374151' }}>
                        {processName(passenger?.originalBookingDoc?.name || passenger?.name || passenger?.passengerInformation?.title || "")}
                    </p>
                    {(passenger?.onwards || passenger?.originalBookingDoc?.onwards || passenger?.inbound) && (
                        <ConnectionBadge
                            onwards={passenger?.onwards || passenger?.originalBookingDoc?.onwards}
                            inbound={passenger?.inbound}
                        />
                    )}
                </div>
            </Col>

            {/* Assistance Requirements */}
            <Col md={isJersey ? 2 : 1}>
                <p style={{ margin: 0, fontSize: '13px', color: usedTheme?.TEXT_COLOR || '#374151' }}>
                    {processAssistance(passenger?.assistance || passenger?.passengerInformation?.assistance || []).join(", ")}
                </p>
            </Col>

            {/* Pre-board or Ramp Status */}
            <Col md={isJersey ? 3 : 2}>
                <PassengerStatusBadge
                    status={getPaxStatus(passenger, isDeparture ? "preBoard" : "ramp", rejectReasons)}
                    showUnrejectBtn={getPaxStatus(passenger, isDeparture ? "preBoard" : "ramp", rejectReasons)?.startsWith("Rejected")}
                    unRejectType={isDeparture ? "preBoard" : "ramp"}
                    unRejectAction={() => handleUnRejectPax(passenger, isDeparture ? "preBoard" : "ramp")}
                    unRejectPaxStatus={unRejectPaxStatus && unRejectPaxStatus === "loading" && passenger?._id === passengerToUnReject?._id}
                />
            </Col>

            {/* Pre-board or Ramp Assigned Employee(s) */}
            <Col md={isJersey ? 3 : 2}>
                {(isDeparture ? passenger?.preEmployees : passenger?.rampEmployees)?.length > 0
                    ? <p style={{ margin: 0, fontSize: '13px', color: usedTheme?.TEXT_COLOR || '#374151' }}>
                        {(isDeparture ? passenger.preEmployees : passenger.rampEmployees).join(", ")}
                    </p>
                    : <PassengerStatusBadge status={"Not Allocated"} showUnrejectBtn={false} unRejectType={""} unRejectAction={() => { }} unRejectPaxStatus={""} />
                }
            </Col>

            {/* Boarding or Arrival Status - hidden for Jersey */}
            {!isJersey && (
                <Col md={2}>
                    <PassengerStatusBadge
                        status={getPaxStatus(passenger, isDeparture ? "boarding" : "arrival", rejectReasons)}
                        showUnrejectBtn={getPaxStatus(passenger, isDeparture ? "boarding" : "arrival", rejectReasons)?.startsWith("Rejected")}
                        unRejectType={isDeparture ? "boarding" : "arrival"}
                        unRejectAction={() => handleUnRejectPax(passenger, isDeparture ? "boarding" : "arrival")}
                        unRejectPaxStatus={unRejectPaxStatus && unRejectPaxStatus === "loading" && passenger?._id === passengerToUnReject?._id}
                    />
                </Col>
            )}

            {/* Boarding or Arrival Assigned Employee(s) - hidden for Jersey */}
            {!isJersey && (
                <Col md={2}>
                    {(isDeparture ? passenger?.boardingEmployees : passenger?.preEmployees)?.length > 0
                        ? <p style={{ margin: 0, fontSize: '13px', color: usedTheme?.TEXT_COLOR || '#374151' }}>
                            {(isDeparture ? passenger.boardingEmployees : passenger.preEmployees).join(", ")}
                        </p>
                        : <PassengerStatusBadge status={"Not Allocated"} showUnrejectBtn={false} unRejectType={""} unRejectAction={() => { }} unRejectPaxStatus={""} />
                    }
                </Col>
            )}

            {/* Action Buttons */}
            <Col md={1} className='d-flex flex-column gap-1'>
                <Button
                    variant='outline-primary'
                    size='sm'
                    style={{ fontSize: '12px', padding: '2px 8px' }}
                    onClick={() => handleEditPassenger(passenger)}
                >
                    Edit
                </Button>
                {/* {onAssign && (
                    <Button
                        variant={isInAssignList ? 'success' : 'outline-success'}
                        size='sm'
                        style={{ fontSize: '12px', padding: '2px 8px' }}
                        onClick={() => onAssign(passenger)}
                        disabled={isInAssignList}
                    >
                        {isInAssignList ? 'Assigned' : 'Assign'}
                    </Button>
                )} */}
                {/* show un reject button for all journey rejected */}
                {getPaxStatus(passenger, isDeparture ? "preBoard" : "ramp", rejectReasons)?.startsWith("All Journey Rejected") && (
                    <Button
                        variant='outline-primary'
                        size='sm'
                        style={{ fontSize: '12px', padding: '2px 8px' }}
                        onClick={() => handleUnRejectPax(passenger)}
                    >
                        {unRejectPaxStatus && unRejectPaxStatus === "loading" && passenger?._id === passengerToUnReject?._id ? <CustomSpinner size='small' /> : "Un Reject"}
                    </Button>
                )}
                {/* show check in and checkout button */}
                {!checkIfRejectedOrServiced() && (
                    checkIfCheckedInPax(passenger)
                        ? <Button variant='outline-danger' size='sm' style={{ fontSize: '12px', padding: '2px 8px' }} onClick={() => handleCheckOut(passenger)}>
                            {(arrivalPaxCheckInStatus === "loading" || paxCheckOutStatus === "loading") && passengerToCheckOut?._id === passenger?._id ? <CustomSpinner size='small' /> : "Check Out"}
                        </Button>
                        : <Button variant='outline-primary' size='sm' style={{ fontSize: '12px', padding: '2px 8px' }} onClick={() => handleCheckIn(passenger)}>Check In</Button>
                )}
            </Col>
        </Row>
    );

    //#endregion
};

export default PassengerRow;